import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Loader2 } from "lucide-react";
import { LeadHoneypot } from "@/components/LeadHoneypot";
import { useLeadSubmit } from "@/hooks/useLeadSubmit";

type Fields = {
  name: string;
  email: string;
  phone: string;
  company: string;
  requirement: string;
  demo_date: string;
  demo_time: string;
  message: string;
};

type Errors = Partial<Record<keyof Fields, string>>;

const REQUIREMENTS = [
  "WCAG Audit",
  "Remediation",
  "VPAT / ACR",
  "Mobile App Accessibility",
  "PDF Accessibility",
  "Other",
];

// The CRM's service field for each requirement (Google Ads reporting).
const REQUIREMENT_SERVICE: Record<string, string | undefined> = {
  "WCAG Audit": "wcag_audit",
  "Remediation": "remediation",
  "VPAT / ACR": "vpat",
  "Mobile App Accessibility": "testing",
  "PDF Accessibility": "pdf",
  "Other": "other",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (f: Fields): Errors => {
  const e: Errors = {};
  if (!f.name.trim()) e.name = "Full name is required";
  if (!f.email.trim()) {
    e.email = "Email is required";
  } else if (!EMAIL_RE.test(f.email)) {
    e.email = "Enter a valid email address";
  }
  if (!f.message.trim()) e.message = "Message is required";
  return e;
};

const ContactSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const [fields, setFields] = useState<Fields>({
    name: "", email: "", phone: "", company: "",
    requirement: "", demo_date: "", demo_time: "", message: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  // Submission failure (`submitError`, from useLeadSubmit) is shown inline in
  // an always-mounted role="alert" region, so screen readers announce it
  // immediately and it stays next to the form (WCAG 4.1.3 / 3.3.1 — NCS-271).

  // Refs to the three validated fields so we can move focus to the first
  // invalid one on submit failure (WCAG 3.3.1 — NCS-252 DF11). Order matches
  // visual order so "first error" is meaningful.
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Shared lead-form logic: anti-spam signals, ad attribution, conversion
  // events and the inline submission error (see useLeadSubmit).
  const { honeypotRef, loading, submitError, markStarted, submit } = useLeadSubmit("contact");

  const set = (key: keyof Fields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      markStarted();
      setFields(f => ({ ...f, [key]: e.target.value }));
      if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errs = validate(fields);
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Move focus to the first invalid field so keyboard and screen-reader
      // users are taken to the error instead of being left on the Submit
      // button (WCAG 3.3.1 — NCS-252 DF11).
      if (errs.name) nameRef.current?.focus();
      else if (errs.email) emailRef.current?.focus();
      else if (errs.message) messageRef.current?.focus();
      return;
    }

    const result = await submit({
      name: fields.name,
      email: fields.email,
      message: fields.message,
      phone: fields.phone,
      company: fields.company,
      requirement: fields.requirement,
      service: REQUIREMENT_SERVICE[fields.requirement],
      demo_time: (fields.demo_date && fields.demo_time)
        ? `${fields.demo_date}T${fields.demo_time}`
        : undefined,
    });
    if (result.ok) setSubmitted(true);
  };

  return (
    <section id="contact" className="section-padding section-alt" aria-labelledby="contact-heading">
      <div className="max-w-7xl mx-auto">
        <p className="eyebrow mb-4">Get in Touch</p>
        <h2 id="contact-heading" className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
          Schedule Your Web Accessibility Audit
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-8">
          Need WCAG audit services, mobile app accessibility testing, accessibility remediation services, or enterprise WCAG solutions? Tell us your requirements — we'll respond within 24 hours.
        </p>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-3 min-w-0">
            {/* Success announcement. Always mounted (empty until submit) so the
                live region already exists when its text arrives — a region
                inserted together with its content is often not announced
                (WCAG 4.1.3 — NCS-271). */}
            <div role="status" className="sr-only">
              {submitted ? "Enquiry received. We'll respond within 24 hours." : ""}
            </div>
            {submitted ? (
              <div className="card-surface p-10 text-center">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Mail size={24} className="text-primary" aria-hidden="true" />
                </div>
                <h3 className="font-display text-xl font-bold text-foreground mb-2">Enquiry Received</h3>
                <p className="text-base text-muted-foreground">We'll respond within 24 hours with a proposed scope, timeline, and quote.</p>
                {fields.demo_date && fields.demo_time && (
                  <p className="text-base text-muted-foreground mt-2">
                    A calendar invite for your requested demo time has been sent to <strong>{fields.email}</strong>.
                  </p>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="card-surface p-6 md:p-8 space-y-5" aria-label="Accessibility audit enquiry form" noValidate>
                {/* Explains the asterisk before the first field (WCAG 3.3.2 —
                    NCS-279). The asterisks themselves stay aria-hidden: the
                    inputs already expose `required` to assistive tech. */}
                <p className="text-base text-muted-foreground">
                  Fields marked with an asterisk (<span className="text-destructive">*</span>) are required.
                </p>

                {/* Anti-spam honeypot — invisible to people and assistive tech. */}
                <LeadHoneypot id="contact-ref-hp" ref={honeypotRef} />

                {/* Name + Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full-name" className="mb-1 block text-sm font-medium text-foreground">
                      Full Name <span className="text-destructive" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="full-name"
                      ref={nameRef}
                      value={fields.name}
                      onChange={set("name")}
                      placeholder="Full Name"
                      autoComplete="name"
                      required
                      aria-required="true"
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "error-name" : undefined}
                    />
                    {errors.name && <p id="error-name" role="alert" className="text-xs text-destructive mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email" className="mb-1 block text-sm font-medium text-foreground">
                      Email <span className="text-destructive" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="email"
                      ref={emailRef}
                      type="email"
                      value={fields.email}
                      onChange={set("email")}
                      placeholder="Email"
                      autoComplete="email"
                      required
                      aria-required="true"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "error-email" : undefined}
                    />
                    {errors.email && <p id="error-email" role="alert" className="text-xs text-destructive mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* Phone + Company */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="mb-1 block text-sm font-medium text-foreground">Phone</Label>
                    <Input id="phone" type="tel" value={fields.phone} onChange={set("phone")} placeholder="Phone" autoComplete="tel" />
                  </div>
                  <div>
                    <Label htmlFor="company" className="mb-1 block text-sm font-medium text-foreground">Company / Entity Name</Label>
                    <Input id="company" value={fields.company} onChange={set("company")} placeholder="Company / Entity Name" autoComplete="organization" />
                  </div>
                </div>

                {/* Requirement */}
                <div>
                  <Label htmlFor="requirement" className="mb-1 block text-sm font-medium text-foreground">Requirement</Label>
                  <select
                    id="requirement"
                    value={fields.requirement}
                    onChange={(e) => { markStarted(); setFields(f => ({ ...f, requirement: e.target.value })); }}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 md:text-sm"
                  >
                    <option value="">Select a requirement</option>
                    {REQUIREMENTS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {/* Demo Date + Time */}
                <div role="group" aria-labelledby="demo-date-time-label" className="rounded-xl border bg-muted/40 p-4">
                  <p id="demo-date-time-label" className="text-sm font-medium text-foreground mb-3">Preferred Demo Date &amp; Time <span className="font-normal text-muted-foreground">(optional)</span></p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="demo-date" className="mb-1 block text-xs font-medium text-muted-foreground">Date</Label>
                      <Input
                        id="demo-date"
                        type="date"
                        className="min-w-0"
                        value={fields.demo_date}
                        onChange={set("demo_date")}
                        min={new Date().toISOString().slice(0, 10)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="demo-time" className="mb-1 block text-xs font-medium text-muted-foreground">Time</Label>
                      <Input
                        id="demo-time"
                        type="time"
                        className="min-w-0"
                        value={fields.demo_time}
                        onChange={set("demo_time")}
                      />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="mb-1 block text-sm font-medium text-foreground">
                    Message <span className="text-destructive" aria-hidden="true">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    ref={messageRef}
                    value={fields.message}
                    onChange={set("message")}
                    placeholder="Your message"
                    rows={4}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "error-message" : undefined}
                  />
                  {errors.message && <p id="error-message" role="alert" className="text-xs text-destructive mt-1">{errors.message}</p>}
                </div>

                <div>
                  <Button variant="hero" size="lg" type="submit" className="w-full min-h-[44px]" disabled={loading}>
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : "Submit Enquiry →"}
                  </Button>
                  {/* Right below Submit, where focus is. Always rendered (never
                      display:none) so it stays in the accessibility tree and
                      its text is announced when set (see submitError). */}
                  <div role="alert" className="mt-2 text-sm text-destructive">
                    {submitError}
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Contact info */}
          <div className="lg:col-span-2 min-w-0 card-surface self-start lg:sticky lg:top-24 p-6 md:p-8 space-y-6">
            <div>
              <h3 className="font-display text-lg font-bold text-foreground mb-4">What happens next</h3>
              <ol className="space-y-4">
                {[
                  ["We reply within 24 hours", "A real person reads every enquiry."],
                  ["You get a proposed scope", "Timeline and quote, based on your site."],
                  ["We schedule your demo", "At the date and time you chose, with a calendar invite."],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-3">
                    <span className="font-mono text-sm font-bold text-primary" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="text-base font-semibold text-foreground">{t}</p>
                      <p className="text-base text-muted-foreground">{d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <h3 className="font-display text-lg font-bold text-foreground border-t pt-6">Prefer to write directly?</h3>
            <div className="flex gap-3 items-start">
              <Mail size={18} className="text-primary mt-1 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">Email</p>
                <a href="mailto:priya@accessbharat.com" className="-my-2 inline-flex min-h-[44px] items-center break-all text-base text-muted-foreground hover:text-primary transition-colors focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm">priya@accessbharat.com</a>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <MapPin size={18} className="text-primary mt-1 shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-foreground">Location</p>
                <p className="text-base text-muted-foreground">Mumbai, Maharashtra</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
